import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { StreamChat } from "stream-chat";
import { useEventSafe } from "./EventContext";
import { useAttendee } from "./AttendeeContext";
import { authenticatedApi } from "../lib/api";

const ChatContext = createContext();

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}

export function ChatProvider({ children }) {
  const [client, setClient] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const initializedRef = useRef(false);
  const { event } = useEventSafe();
  const { attendee } = useAttendee();

  // Get Stream API key from environment
  const streamKey = import.meta.env.VITE_STREAM_KEY;

  // Initialize Stream client
  const initializeClient = useCallback(async () => {
    if (!streamKey || !attendee || !event || initializedRef.current) {
      return;
    }

    try {
      initializedRef.current = true;
      setLoading(true);
      setError(null);

      // Create Stream client
      const streamClient = StreamChat.getInstance(streamKey);

      // Get JWT token from backend
      const tokenData = await authenticatedApi.getStreamToken(event.slug);
      const { token, user_id, user_data } = tokenData;

      // Connect user to Stream with proper JWT token
      await streamClient.connectUser(
        {
          id: user_id,
          name: user_data.name,
          image: user_data.image,
          email: user_data.email,
        },
        token
      );

      setClient(streamClient);
      setIsConnected(true);
    } catch (error) {
      setError(error.message);
      initializedRef.current = false; // Reset on error
    } finally {
      setLoading(false);
    }
  }, [streamKey, attendee, event]);

  // Initialize client when attendee and event are available
  useEffect(() => {
    if (attendee && event && !client && !loading && !initializedRef.current) {
      initializeClient();
    }
  }, [attendee, event, initializeClient, loading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (client) {
        client.disconnectUser();
      }
    };
  }, [client]);

  // Create a 1-1 channel with another attendee
  const createChannel = useCallback(
    async (otherAttendeeId) => {
      if (!client || !attendee) {
        throw new Error("Chat client not initialized");
      }

      try {
        const channelId = `attendee_${attendee.id}_attendee_${otherAttendeeId}`;

        const channel = client.channel("messaging", channelId, {
          name: `Chat with ${otherAttendeeId}`,
          members: [`attendee_${attendee.id}`, `attendee_${otherAttendeeId}`],
        });

        await channel.create();
        return channel;
      } catch (error) {
        throw error;
      }
    },
    [client, attendee]
  );

  // Get or create channel with another attendee
  const getOrCreateChannel = useCallback(
    async (otherAttendeeId) => {
      if (!client || !attendee) {
        throw new Error("Chat client not initialized");
      }

      try {
        const channelId = `attendee_${attendee.id}_attendee_${otherAttendeeId}`;

        // Try to get existing channel
        const channel = client.channel("messaging", channelId);
        await channel.watch();

        return channel;
      } catch (error) {
        // If channel doesn't exist, create it
        if (error.code === 4) {
          // Channel not found
          return await createChannel(otherAttendeeId);
        }
        throw error;
      }
    },
    [client, attendee, createChannel]
  );

  // Get all channels for the current attendee
  const getChannels = useCallback(async () => {
    if (!client || !attendee) {
      return [];
    }

    try {
      const filter = {
        type: "messaging",
        members: { $in: [`attendee_${attendee.id}`] },
      };

      const channels = await client.queryChannels(filter, {
        last_message_at: -1,
      });

      return channels;
    } catch (error) {
      return [];
    }
  }, [client, attendee]);

  const value = {
    client,
    isConnected,
    loading,
    error,
    createChannel,
    getOrCreateChannel,
    getChannels,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
